import React from 'react';
import { useNavigate } from 'react-router-dom';
import { Button } from '@/components/ui/button';
import { Stethoscope, Brain, ClipboardList, TrendingUp, Award, Users } from 'lucide-react';

const LandingPage = ({ user }) => {
  const navigate = useNavigate();

  const features = [
    {
      icon: <Stethoscope className="w-8 h-8" />,
      title: "AI Patient Simulation",
      description: "Interact with diverse virtual patients powered by advanced AI, featuring realistic responses and dynamic scenarios."
    },
    {
      icon: <ClipboardList className="w-8 h-8" />,
      title: "Complete EHR Documentation",
      description: "Practice charting, nursing notes, and clinical documentation in a realistic electronic health record environment."
    },
    {
      icon: <Brain className="w-8 h-8" />,
      title: "Clinical Reasoning & Care Plans",
      description: "Develop nursing diagnoses, create individualized care plans, and practice evidence-based interventions."
    },
    {
      icon: <TrendingUp className="w-8 h-8" />,
      title: "Performance Analytics",
      description: "Track your progress with detailed analytics, competency scores, and NCLEX-aligned feedback."
    },
    {
      icon: <Award className="w-8 h-8" />,
      title: "Professional Feedback",
      description: "Receive customized, evidence-based feedback tailored to your experience level and learning goals."
    },
    {
      icon: <Users className="w-8 h-8" />,
      title: "Multiple Care Settings",
      description: "Practice in diverse environments including med-surg, ICU, long-term care, and rehabilitation units."
    }
  ];

  return (
    <div className="min-h-screen bg-gradient-to-br from-teal-50 to-slate-100 dark:from-slate-900 dark:to-slate-950">
      {/* Navigation */}
      <nav className="border-b border-border bg-white/80 dark:bg-slate-900/80 backdrop-blur-md sticky top-0 z-50">
        <div className="container mx-auto px-6 py-4 flex justify-between items-center">
          <div className="flex items-center gap-3">
            <Stethoscope className="w-8 h-8 text-primary" />
            <span className="text-2xl font-bold text-primary font-['Manrope']">ClinicalSim AI</span>
          </div>
          <div className="flex gap-4">
            {user ? (
              <Button data-testid="dashboard-button" onClick={() => navigate('/dashboard')} className="bg-[#0F766E] hover:bg-[#0d6960] text-white">
                Go to Dashboard
              </Button>
            ) : (
              <>
                <Button data-testid="login-button" variant="outline" onClick={() => navigate('/auth')} className="border-2 border-[#0F766E] text-[#0F766E] hover:bg-[#0F766E] hover:text-white">
                  Log In
                </Button>
                <Button data-testid="signup-button" onClick={() => navigate('/auth')} className="bg-[#0F766E] hover:bg-[#0d6960] text-white">
                  Start Free Trial
                </Button>
              </>
            )}
          </div>
        </div>
      </nav>

      {/* Hero Section */}
      <section className="container mx-auto px-6 py-20 lg:py-32">
        <div className="grid lg:grid-cols-2 gap-12 items-center">
          <div className="space-y-8 bg-white p-8 rounded-2xl shadow-xl border-2 border-teal-100">
            <h1 className="text-4xl md:text-5xl lg:text-6xl font-bold tracking-tight font-['Manrope']" style={{color: '#0f172a', lineHeight: '1.2'}}>
              Master Nursing Skills with{' '}
              <span style={{color: '#0F766E'}}>AI-Powered Simulation</span>
            </h1>
            <p className="text-lg leading-relaxed" style={{color: '#334155', fontWeight: '600', fontSize: '1.125rem'}}>
              Prepare for NCLEX success and build confidence with immersive patient scenarios, 
              evidence-based practice evaluation, and professional-level feedback.
            </p>
            <div className="flex flex-col sm:flex-row gap-4">
              <Button data-testid="get-started-button" size="lg" onClick={() => navigate('/auth')} className="bg-[#0F766E] hover:bg-[#0d6960] text-white font-bold text-lg h-14 px-8 shadow-lg" style={{fontSize: '1.125rem'}}>
                Start Learning Today
              </Button>
              <div className="flex items-center gap-2 text-sm bg-teal-50 px-4 py-3 rounded-lg shadow-md border-2 border-teal-200" style={{fontWeight: '700'}}>
                <span className="font-bold text-base" style={{color: '#0F766E', fontSize: '1.125rem'}}>Only $4.99/month</span>
                <span style={{color: '#0f172a', fontSize: '0.875rem', fontWeight: '600'}}>• Full Access • Cancel Anytime</span>
              </div>
            </div>
          </div>
          <div className="relative rounded-2xl overflow-hidden shadow-2xl border-4 border-teal-600">
            <img 
              src="https://images.pexels.com/photos/5234503/pexels-photo-5234503.jpeg?auto=compress&cs=tinysrgb&w=1200&dpr=2" 
              alt="Nurse using digital tablet" 
              className="w-full h-auto object-cover"
              style={{minHeight: '400px'}}
            />
          </div>
        </div>
      </section>

      {/* Features Grid */}
      <section className="container mx-auto px-6 py-20" style={{backgroundColor: '#ffffff'}}>
        <div className="text-center mb-16">
          <h2 className="text-3xl md:text-4xl font-semibold mb-4 font-['Manrope']" style={{color: '#0f172a'}}>Everything You Need to Excel</h2>
          <p className="text-lg max-w-2xl mx-auto" style={{color: '#334155', fontWeight: '600'}}>
            Comprehensive tools and features designed for nursing students and professionals at all levels
          </p>
        </div>
        <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-8">
          {features.map((feature, index) => (
            <div key={index} className="medical-card p-6 space-y-4" data-testid={`feature-card-${index}`} style={{backgroundColor: 'white', border: '2px solid #e2e8f0'}}>
              <div className="w-14 h-14 rounded-lg bg-primary/10 flex items-center justify-center text-primary">
                {feature.icon}
              </div>
              <h3 className="text-xl font-semibold font-['Manrope']" style={{color: '#0f172a'}}>{feature.title}</h3>
              <p className="leading-relaxed" style={{color: '#334155', fontWeight: '500'}}>{feature.description}</p>
            </div>
          ))}
        </div>
      </section>

      {/* Pricing CTA */}
      <section className="container mx-auto px-6 py-20">
        <div className="medical-card p-12 text-center space-y-6 max-w-3xl mx-auto" style={{backgroundColor: 'white', border: '2px solid #e2e8f0'}}>
          <h2 className="text-3xl md:text-4xl font-semibold font-['Manrope']" style={{color: '#0f172a'}}>Ready to Transform Your Nursing Practice?</h2>
          <p className="text-lg" style={{color: '#334155', fontWeight: '600'}}>
            Join hundreds of nursing students and professionals who are mastering clinical skills with ClinicalSim AI
          </p>
          <div className="pt-6">
            <div className="inline-block">
              <div className="text-5xl font-bold text-primary mb-2"><span style={{color: '#0F766E'}}>$4.99</span><span className="text-2xl" style={{color: '#64748b', fontWeight: '600'}}>/month</span></div>
              <div className="text-sm mb-6" style={{color: '#64748b', fontWeight: '600'}}>Cancel anytime • No hidden fees</div>
              <Button data-testid="cta-signup-button" size="lg" onClick={() => navigate('/auth')} className="bg-[#0F766E] hover:bg-[#0d6960] text-white font-bold text-lg h-12 px-12 shadow-lg">
                Get Started Now
              </Button>
            </div>
          </div>
        </div>
      </section>

      {/* Footer */}
      <footer className="border-t py-12" style={{backgroundColor: 'white', borderColor: '#e2e8f0'}}>
        <div className="container mx-auto px-6 text-center" style={{color: '#64748b', fontWeight: '600'}}>
          <p>© 2024 ClinicalSim AI. All rights reserved.</p>
        </div>
      </footer>
    </div>
  );
};

export default LandingPage;